import { Component, ChangeDetectorRef, Input, SimpleChanges, OnChanges } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { CommonService } from '../../services/common/common.service';
import { ToastrService } from 'ngx-toastr';

interface Project {
  projectId: number;
  projectName: string;
}

interface RowData {
  mappingId?: number;
  Id?: number;
  name: string;
  jobRole?: string;
  description?: string;
  eligibleStaffingCosts: number;
  rAndDEligibility: number[];
  UtilityCost?: number;
}

interface TableData {
  title: string;
  rows: RowData[];
  projects: Project[];
}

@Component({
  selector: 'app-common-cost-form',
  templateUrl: './common-costing-form.component.html',
  styleUrls: ['./common-costing-form.component.scss']
})

export class CommonCostFormComponent implements OnChanges {

  @Input() loadDataAPI!: string;
  @Input() dropDownAPI!: string;
  @Input() requiredDropdownList!: boolean;
  @Input() displayedColumnsShow!: string[];
  @Input() isDisplayColumnSend!: boolean;
  @Input() isUtility: boolean = false;
  @Input() titleForm!: string;
  @Input() isSubContractor: boolean = false;
  @Input() isText: boolean = false;
  @Input() isEpwCostByProject: boolean = false;
  @Input() isConnectedEpwbyProject: boolean = false;
  @Input() addButtonName: string = '';
  @Input() isContractor: boolean = false;
  @Input() jobRoleAPI!: string;
  @Input() isNeedToBindJobRole: boolean = true;
  @Input() jobRoleAndCost!: string;
  @Input() submitDataAPI!: string
  dropDownList: any;
  table: TableData = { title: '', rows: [], projects: [] };
  displayedColumns: string[] = this.displayedColumnsShow;
  form: FormGroup;
  isLoading: boolean = true;
  errorMessages: string[] = []

  constructor(
    private readonly fb: FormBuilder,
    private readonly cdr: ChangeDetectorRef,
    private readonly spinner: NgxSpinnerService,
    private readonly commonService: CommonService,
    private readonly toastr: ToastrService,
  ) {
    this.form = this.fb.group({
      name: [''],
      jobRole: [''],
      description: ['']
    });

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['loadDataAPI'] || changes['dropDownAPI'] || changes['requiredDropdownList'] || changes['displayedColumnsShow']) {
      this.initializeComponent();
    }
  }

  private initializeComponent(): void {
    this.loadData();
  }

  loadData(): void {
    if (this.requiredDropdownList) {
      this.getDropDownList();
    }
    const apiUrl = this.loadDataAPI;
    this.spinner.show();
    this.commonService.doGet(apiUrl).subscribe({
      next: (response: any) => {
        if (response.success) {
          // Filter unique projects
          this.spinner.hide();
          const uniqueProjects = Array.from(new Set(response.data.map((item: any) => item.projectId)))
            .map(projectId => response.data.find((item: any) => item.projectId === projectId))
            .map((item: any) => ({
              projectId: item.projectId,
              projectName: item.projectName
            }));
          // Initialize the rows with the correct rAndDEligibility based on the unique projects
          const rows: RowData[] = response.data.map((item: any) => {
            const rAndDEligibilityArray = Array(uniqueProjects.length).fill('0');
            uniqueProjects.forEach((project, index) => {
              if (item.projectId === project.projectId) {
                rAndDEligibilityArray[index] = item.rAndDEligibility || '0';
              }
            });
            return {
              mappingId: item.mappingId,
              name: item.name || '',
              jobRole: item.jobRole || '',
              description: item.description || '',
              eligibleStaffingCosts: item.eligibleStaffingCosts || '',
              rAndDEligibility: rAndDEligibilityArray,
              Id: item.id || 0,
              UtilityCost: item?.utilityCost
            };
          });
          // Update job roles and costs after initializing rows
          this.updateJobRolesAndCosts(rows);
          this.table = {
            title: 'Staffing Costs by Project',
            rows: rows,
            projects: uniqueProjects
          };
          this.updateDisplayedColumns();
          this.isLoading = false;
          this.isTableEmpty();
        } else {
          this.isLoading = false;
        }
      },
      error: (err) => {
        this.spinner.hide();
        this.isLoading = false;
      }
    });
  }

  // Method to update job roles and costs after loading data
  updateJobRolesAndCosts(rows: RowData[]): void {
    if (this.isNeedToBindJobRole) {
      rows.forEach(row => {
        this.getJobRoleAndCost(row.Id, row);
      });
    }
  }

  addRow(): void {
    const newRow: RowData = {
      name: '',
      jobRole: '',
      description: '',
      eligibleStaffingCosts: 0,
      rAndDEligibility: Array(this.table.projects.length).fill('0')
    };
    this.table.rows.push(newRow);
    this.updateDisplayedColumns();
    this.cdr.detectChanges();
  }

  removeRow(index: number): void {
    if (this.table.rows.length > 0) {
      this.table.rows.splice(index, 1);
      this.updateDisplayedColumns();
    }
  }

  addColumn(): void {
    const newProjectId = this.table.projects.length + 1;
    const newProjectName = `Project ${newProjectId}`;
    this.table.projects.push({ projectId: newProjectId, projectName: newProjectName });
    this.table.rows.forEach(row => {
      row.rAndDEligibility.push(0);
    });
    this.updateDisplayedColumns();
  }

  isTableEmpty(): boolean {
    return this.table.rows.length === 0 && this.table.projects.length === 0;
  }

  updateDisplayedColumns(): void {
    if (Array.isArray(this.displayedColumnsShow)) {
      this.displayedColumns = [
        ...this.displayedColumnsShow,
        ...this.table.projects.map((_, i) => `project${i}`),
        'total'
      ];
    }
    this.table.rows = [...this.table.rows];
  }

  calculateTotal(row: RowData): number {
    const sumEligibility = row.rAndDEligibility.reduce<number>((acc, val) => acc + (typeof val === 'number' ? val : Number(val)), 0);
    return (sumEligibility * row.eligibleStaffingCosts) / 100;
  }

  calculateProjectTotal(projectIndex: number): number {
    return this.table.rows.reduce((total, row) => {
      const eligibility = row.rAndDEligibility[projectIndex] || 0;
      return total + (eligibility * row.eligibleStaffingCosts) / 100;
    }, 0);
  }

  calculateOverallTotal(): number {
    return this.table.rows.reduce((total, row) => total + this.calculateTotal(row), 0);
  }

  calculateOverallTotalColumn(): number {
    return this.table.rows.reduce((sum, row) => sum + this.calculateTotal(row), 0);
  }

  getDropDownList() {
    const api = this.dropDownAPI;
    this.spinner.show();
    this.commonService.doGet(api).pipe().subscribe({
      next: (response) => {
        if (response.success) {
          this.dropDownList = response.data;
          this.spinner.hide();
        }
      },
      error: (err) => {
        this.spinner.hide();
      },
      complete: () => {
        this.spinner.hide();
      },
    });
  }

  getJobRoleAndCost(Id: any, row: any) {
    if (this.isNeedToBindJobRole) {
      const api = this.jobRoleAndCost.replace('{Id}', Id);
      this.spinner.show();
      this.commonService.doGet(api).pipe().subscribe({
        next: (response) => {
          if (response.success) {
            row.jobRole = response.data[0].jobRole;
            row.eligibleStaffingCosts = response.data[0].cost;
            this.spinner.hide();
          }
        },
        error: (err) => {
          this.spinner.hide();
        },
        complete: () => {
          this.spinner.hide();
        },
      });
    }
    else {
      return;
    }
  }

  getEligibleCost(employeeId: any, row: any) {
    if (this.isNeedToBindJobRole) {
      const api = this.jobRoleAPI.replace('{Id}', employeeId);
      this.spinner.show();
      this.commonService.doGet(api).pipe().subscribe({
        next: (response) => {
          if (response.success) {

            row.jobRole = response.data;
            this.spinner.hide();
          }
        },
        error: (err) => {
          this.spinner.hide();
        },
        complete: () => {
          this.spinner.hide();
        },
      });
    }
    else {
      return;
    }
  }

  calculateColumnTotals(): string[] {
    const columnTotals = new Array(this.table.projects.length).fill(0);
    // Sum up the values for each column
    this.table.rows.forEach(row => {
      const eligibility = row.eligibleStaffingCosts || 0; // Default to 0 if undefined
      row.rAndDEligibility.forEach((rAndDEligibility, index) => {
        const percentage = Number.parseFloat(rAndDEligibility as any) || 0; // Convert to number
        columnTotals[index] += (eligibility * percentage) / 100; // Apply the formula
      });
    });
    // Format each total as a string with 2 decimal places
    return columnTotals.map(value => value.toFixed(2)); // Convert to string with 2 decimal places
  }

  calculateEligibleStaffingCostsSum(): number {
    return this.table.rows.reduce((sum, row) => sum + Number(row.eligibleStaffingCosts || 0), 0);
  }

  submitData(): void {
    // Validate each row
    this.table.rows.forEach((row, rowIndex) => {
      if (!row.mappingId) {
        this.errorMessages.push(`Row ${rowIndex + 1}: Please select from drop down`);
      }
      if (!row.description && this.isText) {
        this.errorMessages.push(`Row ${rowIndex + 1}: Description is missing.`);
      }
      row.rAndDEligibility.forEach((val, projectIndex) => {
        if (val === undefined || val === null) {
          this.errorMessages.push(`Row ${rowIndex + 1}, Project ${projectIndex + 1}: R&D Eligibility percentage is missing.`);
        }
      });
    });
    // If there are validation errors, show them and stop the submission
    if (this.errorMessages.length > 0) {
      this.toastr.error(this.errorMessages.join(' '));
      this.errorMessages = [];
      return;
    }
    let api = this.submitDataAPI;
    // Create the data object to submit
    const dataToSubmit = {
      tableId: 0,
      userId: 0,
      companyId: 0,
      formRawData: this.table.rows.flatMap((row, rowIndex) =>
        this.table.projects.map((project, projectIndex) => ({
          Id: row.Id ?? 0,
          MapppingId: row.mappingId ?? 0,
          ProjectId: project.projectId ?? 0,
          CompanyId: 0,
          IsPercentage: true,
          Value: Number(row.rAndDEligibility[projectIndex] ?? 0),
          Description: row.description ?? '',
        }))
      )
    };
    this.commonService.doPost(api, dataToSubmit).pipe().subscribe({
      next: (response) => {
        if (response.success) {
          this.toastr.success(response.message);
          this.loadData();
        } else {
          this.toastr.success(response.message);
        }
      },
      error: (error) => {
        console.log(error);
      },
      complete: () => {
      },
    });
  }

  removeColumn(index: number): void {
    if (this.table.projects.length > 0) {
      this.table.projects.splice(index, 1);
      this.table.rows.forEach(row => {
        row.rAndDEligibility.splice(index, 1);
      });
      this.updateDisplayedColumns();
    }
  }
}